import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { CopyToClipboardButton, Text } from '@frontend/design-system';

interface Props {
  label: string;
  title?: ReactNode;
  content?: ReactNode;
  isArchived?: boolean;
  leftElement?: ReactNode;
  rightElement?: React.ReactNode;
  children?: React.ReactNode;
  copyableText?: string;
}

export const DetailItem = ({
  label,
  leftElement,
  rightElement,
  title,
  isArchived = false,
  content,
  copyableText,
}: Props) => {
  const { t } = useTranslation('schedule');

  return (
    <fieldset css={[styles.fieldSet, styles.showRightElementOnHover]}>
      <legend css={styles.legend}>{label}</legend>
      <div css={contentItemContainer}>
        <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div css={{ display: 'flex', gap: theme.spacing(0.5), alignItems: 'center' }}>
            {leftElement}
            {title ? (
              typeof title === 'string' ? (
                <DetailItemHeader title={title} isArchived={isArchived} />
              ) : (
                title
              )
            ) : typeof title === 'string' ? (
              <Text size='medium' weight='bold'>
                -
              </Text>
            ) : undefined}
          </div>
          <div css={styles.rightElement}>
            {copyableText && (
              <CopyToClipboardButton
                css={{ height: 24, width: 24 }}
                className='copy-to-clipboard-btn'
                variant='tertiary'
                textToCopy={copyableText}
                hasSuccessTooltip
                tooltipSuccessText={t('Copied')}
              />
            )}
            {rightElement}
          </div>
        </div>
        {typeof content === 'string' ? (
          <Text size='small' color='light' css={styles.stringContent}>
            {content || '-'}
          </Text>
        ) : (
          content
        )}
      </div>
    </fieldset>
  );
};

const DetailItemHeader = ({ title, isArchived }: { title: string; isArchived?: boolean }) =>
  isArchived ? (
    <div css={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
      <Text size='medium' weight='bold' color={isArchived ? 'subdued' : undefined}>
        {title}
      </Text>
      {isArchived && <Icon name='archive-small' color='subdued' size={12} />}
    </div>
  ) : (
    <Text size='medium' weight='bold'>
      {title}
    </Text>
  );

const styles = {
  fieldSet: css`
    padding: 0;
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.medium};
  `,
  showRightElementOnHover: css`
    .copy-to-clipboard-btn {
      opacity: 0;
      transition: opacity 150ms ease;
    }

    &:hover .copy-to-clipboard-btn {
      opacity: 1;
    }
  `,
  rightElement: css`
    display: inline-flex;
    align-items: center;
    transition: opacity 150ms ease;
    gap: ${theme.spacing(1)};
  `,
  legend: css`
    margin-left: ${theme.spacing(4)};
    padding: 0 ${theme.spacing(0.5)};
    font-size: ${theme.font.size.small};
    color: ${theme.colors.neutral50};
  `,
  stringContent: css`
    margin-left: ${theme.spacing(2.5)};
  `,
};

const contentItemContainer = css({
  padding: theme.spacing(1, 2, 2),
  display: 'flex',
  flexDirection: 'column',
});
